import { Alert, Divider, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Getter, Setter, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { motion } from 'motion/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { Loader } from '../../../components/Loader/Loader';
import { ContainerName } from '../../../constants/styles';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import {
  checkCaptableExists,
  getCapTablesForCompany,
  getCaptableVersions,
} from '../../../services/queries/MaggieCapTableQueries';
import {
  captablesByCompanyStateJ,
  captableVersionsByCompanyStateJ,
  selectedCaptableIdStateJ,
  selectedCaptableStateJ,
} from '../../../services/state/CapTableDataStateJ';
import { LoadingStatus } from '../../../types';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { useAsync } from '../../../util/hook-utils';
import { CPWrapper } from '../components/CompanyProfilesWrapper';
import { selectedCompanyIdProfileAtom } from '../state/UIStateJ';
import { AllCaptablesMenu } from './AllCaptableActions/AllCaptablesMenu';
import { CartaActionsMenu } from './CartaActionsMenu';
import { resolveSelectedVersion } from './captable-utils';
import { NoCapTable } from './NoCapTable';
import { CaptableSelector } from './SummaryGrid/CaptableSelector';
import { CaptableSummary } from './SummaryGrid/CaptableSummary';
import { CaptableKpiSummary } from './CaptableKpiSummary';

export const Wrapper = styled('div')`
  margin-top: 24px;
  container-name: ${ContainerName.captableContainer};
  container-type: inline-size;
`;

function useFetchCaptablesLegacy(companyId: number, enabled: boolean) {
  const setCaptables = useSetAtom(captablesByCompanyStateJ(companyId));
  const fetchFn = useCallback(() => {
    if (!enabled) {
      return Promise.resolve(null);
    }
    return checkCaptableExists(companyId).then(async (exists) => {
      if (!exists) {
        return false;
      }
      const captables = await getCapTablesForCompany(companyId);
      setCaptables(captables);
      return true;
    });
  }, [companyId, setCaptables, enabled]);
  return useAsync(fetchFn, { id: `legacy-${companyId}-${enabled}` });
}

export function useFetchCaptableVersions(companyId: number, enabled: boolean) {
  const fetchFn = useAtomCallback(
    useCallback(
      async (get: Getter, set: Setter) => {
        if (!enabled) {
          return null;
        }
        const groups = await getCaptableVersions(companyId);
        await get(captableVersionsByCompanyStateJ(companyId));
        set(captableVersionsByCompanyStateJ(companyId), groups);

        // The current cap table per source (full models) comes from the existing single
        // per-company fetch (`captablesByCompanyStateJ`'s default), not one request per source. It
        // keeps the source picklist, delete, and create/edit flows working; a selected historical
        // version that isn't current is resolved by id via `selectedCaptableStateJ`.
        const currentCaptables = (await get(captablesByCompanyStateJ(companyId))) ?? [];

        // Initialize/repair only: mutations own the selection (see `useInvalidateCaptables`), and
        // this runs concurrently with them on remounts and suspense re-arms. Overwriting a live
        // selection here is the lost update that reverted a just-created captable to the primary.
        const current = await get(selectedCaptableStateJ(companyId));
        if (current == null) {
          const selected = resolveSelectedVersion(groups, null);
          await get(selectedCaptableIdStateJ(companyId));
          set(selectedCaptableIdStateJ(companyId), selected?.id ?? null);
        }

        return currentCaptables.length > 0;
      },
      [companyId, enabled]
    )
  );

  return useAsync(fetchFn, { id: `versions-${companyId}-${enabled}` });
}

export const CompanyProfileCapTable = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const { asOfCapTables } = useFlags<MaggieFeatureFlags>();

  const legacy = useFetchCaptablesLegacy(companyId, !asOfCapTables);
  const versions = useFetchCaptableVersions(companyId, !!asOfCapTables);
  const { data: captableExists, loading: captableLoadingStatus } = asOfCapTables ? versions : legacy;

  if (captableLoadingStatus === LoadingStatus.error) {
    return (
      <Alert severity='error' style={{ alignSelf: 'start' }}>
        Error loading cap table data.
      </Alert>
    );
  }
  if (captableLoadingStatus !== LoadingStatus.success) {
    return <Loader />;
  }
  return (
    <CPWrapper>
      <Wrapper>{captableExists ? <CaptableMainSection /> : <NoCapTable />}</Wrapper>
    </CPWrapper>
  );
};

export function CaptableMainSection() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const selectedCaptable = useAtomValue(selectedCaptableStateJ(companyId));

  const canEditCaptable = PermissionService.get().hasPermission(PermissionKey.canWriteCaptable);
  const hasCustomerSuccessAccess = PermissionService.get().hasPermission(PermissionKey.customerSuccessAll);

  return (
    <motion.div animate={{ opacity: [0.6, 1] }} transition={{ duration: 0.5 }} key={selectedCaptable?.id}>
      <Stack
        display={'grid'}
        gridTemplateColumns={'1fr auto'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <CaptableSelector />
        <Stack display={'grid'} gridAutoFlow={'column'} gap='1rem' alignItems={'center'}>
          {canEditCaptable && <AllCaptablesMenu />}
          {hasCustomerSuccessAccess && <CartaActionsMenu />}
        </Stack>
      </Stack>
      <Divider style={{ marginBlock: '0.75rem' }} />
      {selectedCaptable && (
        <>
          <CaptableKpiSummary captable={selectedCaptable} />
          <CaptableSummary data={selectedCaptable} />
        </>
      )}
    </motion.div>
  );
}
